class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('ul');
    this.sliderItems = this.querySelectorAll('li');
    this.pageCount = this.querySelector('.slider-counter--current');
    this.pageTotal = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progress = this.querySelector('.cosmoshop-slider-progress');
    this.progressBar = this.querySelector('.cosmoshop-slider-progress__bar');
    this.desktopDragDisabledSelector = 'body.template-index .product_tab-block slider-component, body.template-index .collection.special slider-component, body.template-index .featured-blog slider-component, body.template-index .collection-list-wrapper slider-component';

    if (!this.slider) return;

    this.productCarousel = this.isProductCarousel();

    // Product carousels use discrete wheel navigation instead of free mouse dragging.
    if (this.productCarousel) {
      this.enableProductCarouselWheelScroll();
    } else {
      this.enableSwipeDrag();
    }

    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.progress) {
      this.progress.addEventListener('click', this.onProgressClick.bind(this));
    }

    window.requestAnimationFrame(() => this.initPages());
  }

  initPages() {
    if (this.sliderItems.length === 0) return;

    const itemWidth = this.sliderItems[0].clientWidth || 1;
    const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);

    this.slidesPerPage = Math.max(1, Math.floor(this.slider.clientWidth / itemWidth));
    this.totalPages = Math.max(1, Math.ceil(maxScrollLeft / itemWidth) + 1);
    this.update();
  }

  update() {
    if (this.sliderItems.length === 0) return;

    // Keep the smooth-wheel destination in sync after native touch swipes or
    // progress-thumb dragging, so a later wheel gesture always starts exactly
    // from the user's current free-scroll position.
    if (
      this.isTabCollectionWheelCarousel &&
      !this.wheelAnimationFrame &&
      this.progressDragPointerId === null
    ) {
      this.tabWheelTarget = this.slider.scrollLeft;
    }

    const itemWidth = this.sliderItems[0].clientWidth || 1;
    const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const tolerance = 2;
    const atStart = this.slider.scrollLeft <= tolerance;
    const atEnd = this.slider.scrollLeft >= maxScrollLeft - tolerance;

    this.currentPage = Math.round(this.slider.scrollLeft / itemWidth) + 1;
    if (atEnd) this.currentPage = this.totalPages;
    this.currentPage = Math.min(Math.max(this.currentPage, 1), this.totalPages);

    const shouldHidePrevButton = atStart || this.totalPages <= 1;
    const shouldHideNextButton = atEnd || this.totalPages <= 1;

    if (this.prevButton) {
      this.prevButton.toggleAttribute('disabled', shouldHidePrevButton);
      this.prevButton.toggleAttribute('hidden', shouldHidePrevButton);
      this.prevButton.setAttribute('aria-hidden', shouldHidePrevButton ? 'true' : 'false');
    }

    if (this.nextButton) {
      this.nextButton.toggleAttribute('disabled', shouldHideNextButton);
      this.nextButton.toggleAttribute('hidden', shouldHideNextButton);
      this.nextButton.setAttribute('aria-hidden', shouldHideNextButton ? 'true' : 'false');
    }

    const sliderButtons = this.querySelector('.slider-buttons');
    if (sliderButtons) {
      sliderButtons.classList.toggle('slider-buttons--prev-hidden', shouldHidePrevButton);
      sliderButtons.classList.toggle('slider-buttons--next-hidden', shouldHideNextButton);
    }

    this.updateProgress(maxScrollLeft);
    this.updateProductCarouselWheelProgress(maxScrollLeft);

    if (this.pageCount && this.pageTotal) {
      this.pageCount.textContent = this.currentPage;
      this.pageTotal.textContent = this.totalPages;
    }
  }

  updateProgress(maxScrollLeft) {
    if (!this.progress || !this.progressBar) return;

    const trackWidth = this.progress.clientWidth;
    const scrollWidth = this.slider.scrollWidth || 1;
    const clientWidth = this.slider.clientWidth || 1;

    if (maxScrollLeft <= 2) {
      this.progress.classList.add('is-hidden');
      return;
    }

    if (trackWidth <= 0) return;

    this.progress.classList.remove('is-hidden');

    const ratio = Math.min(1, clientWidth / scrollWidth);
    const thumbWidth = Math.max(26, Math.round(trackWidth * ratio) - 2);
    const progress = Math.min(1, Math.max(0, this.slider.scrollLeft / maxScrollLeft));
    const thumbOffset = Math.round((trackWidth - thumbWidth) * progress);

    this.progressBar.style.width = `${thumbWidth}px`;
    this.progressBar.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
  }

  onProgressClick(event) {
    if (!this.progress || !this.slider) return;

    const rect = this.progress.getBoundingClientRect();
    const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    if (maxScrollLeft <= 0 || rect.width <= 0) return;

    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    this.slider.scrollTo({
      left: maxScrollLeft * ratio,
      behavior: 'smooth'
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.sliderItems.length === 0) return;

    const slideScrollPosition = event.currentTarget.name === 'next'
      ? this.slider.scrollLeft + this.sliderItems[0].clientWidth
      : this.slider.scrollLeft - this.sliderItems[0].clientWidth;

    this.slider.scrollTo({
      left: slideScrollPosition,
      behavior: 'smooth'
    });
  }

  enableProductCarouselWheelScroll() {
    this.classList.add('product-carousel--wheel-snap');
    this.isTabCollectionWheelCarousel = Boolean(this.closest('.product_tab-block'));
    this.ensureProductCarouselWheelProgress();
    this.enableProductCarouselProgressDrag();

    this.wheelStepDirection = 0;
    this.wheelStepLocked = false;
    this.wheelSnapTarget = null;
    this.wheelAnimationFrame = null;
    this.wheelGestureReleaseTimer = null;
    this.wheelDeltaAccumulator = 0;
    this.wheelLastEventTime = 0;
    this.wheelLastDirection = 0;
    this.tabWheelTarget = null;
    this.tabWheelLastFrameTime = 0;
    this.productCarouselTouchQuery = window.matchMedia
      ? window.matchMedia('(any-pointer: coarse), (hover: none)')
      : null;

    // The 990px breakpoint remains a layout decision only. Carousel input
    // follows the user's actual hardware: touch-capable devices get native
    // finger swiping at any viewport width, while wheel events still use the
    // exact one-card desktop animation.
    this.updateProductCarouselInteractionMode();

    const handleInteractionModeChange = () => {
      this.updateProductCarouselInteractionMode();
    };

    if (this.productCarouselTouchQuery) {
      if (this.productCarouselTouchQuery.addEventListener) {
        this.productCarouselTouchQuery.addEventListener('change', handleInteractionModeChange);
      } else if (this.productCarouselTouchQuery.addListener) {
        // Compatibility for older iOS Safari versions.
        this.productCarouselTouchQuery.addListener(handleInteractionModeChange);
      }
    }

    this.slider.addEventListener('wheel', this.onProductCarouselWheel.bind(this), { passive: false });
  }

  isProductCarousel() {
    return Boolean(
      this.hasAttribute('data-cosmoshop-wheel-carousel') ||
      this.slider.querySelector('.products, .card-wrapper')
    );
  }

  ensureProductCarouselWheelProgress() {
    let progress = this.querySelector('.cosmoshop-wheel-progress');

    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'cosmoshop-wheel-progress';
      progress.setAttribute('aria-hidden', 'true');

      const bar = document.createElement('span');
      bar.className = 'cosmoshop-wheel-progress__bar';
      progress.appendChild(bar);
      this.appendChild(progress);
    }

    this.wheelProgress = progress;
    this.wheelProgressBar = progress.querySelector('.cosmoshop-wheel-progress__bar');
  }

  enableProductCarouselProgressDrag() {
    if (!this.isTabCollectionWheelCarousel || !this.wheelProgress || !this.wheelProgressBar) return;
    if (this.wheelProgressBar.dataset.cosmoshopProgressDragReady === 'true') return;

    this.wheelProgressBar.dataset.cosmoshopProgressDragReady = 'true';
    this.progressDragPointerId = null;
    this.progressDragStartX = 0;
    this.progressDragStartScrollLeft = 0;
    this.progressDragScale = 0;

    this.wheelProgressBar.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;

      const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      const trackWidth = this.wheelProgress.clientWidth;
      const thumbWidth = this.wheelProgressBar.getBoundingClientRect().width;
      const thumbTravel = Math.max(0, trackWidth - thumbWidth);

      if (maxScrollLeft <= 0 || thumbTravel <= 0) return;

      event.preventDefault();
      this.cancelProductCarouselWheel();
      this.disableProductCarouselSnap();

      this.progressDragPointerId = event.pointerId;
      this.progressDragStartX = event.clientX;
      this.progressDragStartScrollLeft = this.slider.scrollLeft;
      this.progressDragScale = maxScrollLeft / thumbTravel;
      this.wheelProgress.classList.add('is-dragging');

      if (this.wheelProgressBar.setPointerCapture) {
        this.wheelProgressBar.setPointerCapture(event.pointerId);
      }
    });

    this.wheelProgressBar.addEventListener('pointermove', (event) => {
      if (this.progressDragPointerId === null || event.pointerId !== this.progressDragPointerId) return;

      event.preventDefault();
      const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      const deltaX = event.clientX - this.progressDragStartX;
      const target = Math.max(0, Math.min(maxScrollLeft, this.progressDragStartScrollLeft + (deltaX * this.progressDragScale)));

      this.slider.scrollLeft = target;
      this.updateProductCarouselWheelProgress(maxScrollLeft);
    });

    const finishProgressDrag = (event) => {
      if (this.progressDragPointerId === null || event.pointerId !== this.progressDragPointerId) return;

      const pointerId = this.progressDragPointerId;
      this.progressDragPointerId = null;
      this.wheelProgress.classList.remove('is-dragging');

      if (this.wheelProgressBar.hasPointerCapture && this.wheelProgressBar.hasPointerCapture(pointerId)) {
        this.wheelProgressBar.releasePointerCapture(pointerId);
      }

      // Homepage tab collections intentionally remain exactly where the user releases
      // the thumb. This mirrors the collection pagination's free-scroll behaviour.
      this.updateProductCarouselWheelProgress(
        Math.max(0, this.slider.scrollWidth - this.slider.clientWidth)
      );
    };

    this.wheelProgressBar.addEventListener('pointerup', finishProgressDrag);
    this.wheelProgressBar.addEventListener('pointercancel', finishProgressDrag);
  }

  updateProductCarouselWheelProgress(maxScrollLeft) {
    if (!this.productCarousel || !this.wheelProgress || !this.wheelProgressBar) return;

    if (maxScrollLeft <= 2) {
      this.wheelProgress.classList.add('is-hidden');
      return;
    }

    const trackWidth = this.wheelProgress.clientWidth;
    const scrollWidth = this.slider.scrollWidth || 1;
    const clientWidth = this.slider.clientWidth || 1;

    if (trackWidth <= 0) return;

    this.wheelProgress.classList.remove('is-hidden');

    const visibleRatio = Math.min(1, clientWidth / scrollWidth);
    const barWidth = Math.max(26, Math.round(trackWidth * visibleRatio));
    const scrollProgress = Math.min(1, Math.max(0, this.slider.scrollLeft / maxScrollLeft));
    const barOffset = Math.round((trackWidth - barWidth) * scrollProgress);

    this.wheelProgressBar.style.width = `${barWidth}px`;
    this.wheelProgressBar.style.transform = `translate3d(${barOffset}px, 0, 0)`;
  }

  getProductCarouselSnapPositions() {
    const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const sliderRect = this.slider.getBoundingClientRect();
    const positions = [];

    this.sliderItems.forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const rawPosition = itemRect.left - sliderRect.left + this.slider.scrollLeft;
      const position = Math.min(maxScrollLeft, Math.max(0, rawPosition));
      const previousPosition = positions[positions.length - 1];

      if (previousPosition === undefined || Math.abs(position - previousPosition) > 1) {
        positions.push(position);
      }
    });

    if (positions.length === 0 || positions[0] > 1) positions.unshift(0);
    if (Math.abs(positions[positions.length - 1] - maxScrollLeft) > 1) positions.push(maxScrollLeft);

    return positions;
  }

  getProductCarouselStepTarget(direction, basePosition) {
    const positions = this.getProductCarouselSnapPositions();
    const tolerance = 2;

    if (direction > 0) {
      return positions.find((position) => position > basePosition + tolerance) ?? null;
    }

    for (let index = positions.length - 1; index >= 0; index -= 1) {
      if (positions[index] < basePosition - tolerance) return positions[index];
    }

    return null;
  }

  onProductCarouselWheel(event) {
    if (event.ctrlKey) return;

    const maxScrollLeft = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    if (maxScrollLeft <= 2) return;

    let dominantDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

    if (event.deltaMode === 1) dominantDelta *= 16;
    if (event.deltaMode === 2) dominantDelta *= Math.max(1, this.slider.clientWidth);
    if (Math.abs(dominantDelta) < 0.5) return;

    // Homepage tab collections remain true free-scrolling strips: wheel/trackpad
    // distance is preserved, but the raw wheel deltas are eased through a single
    // requestAnimationFrame loop so discrete mouse-wheel events do not feel jerky.
    if (this.isTabCollectionWheelCarousel) {
      const basePosition = this.tabWheelTarget === null
        ? this.slider.scrollLeft
        : this.tabWheelTarget;
      const atStart = basePosition <= 0;
      const atEnd = basePosition >= maxScrollLeft - 1;

      // At either end, return the wheel to normal page scrolling.
      if ((dominantDelta < 0 && atStart) || (dominantDelta > 0 && atEnd)) return;

      event.preventDefault();
      this.slider.style.setProperty('scroll-behavior', 'auto', 'important');
      this.slider.style.setProperty('scroll-snap-type', 'none', 'important');

      this.tabWheelTarget = Math.max(
        0,
        Math.min(maxScrollLeft, basePosition + (dominantDelta * 2))
      );

      if (!this.wheelAnimationFrame) {
        this.tabWheelLastFrameTime = performance.now();

        const animateTabWheel = (currentTime) => {
          const target = this.tabWheelTarget;
          if (target === null) {
            this.wheelAnimationFrame = null;
            return;
          }

          const current = this.slider.scrollLeft;
          const distance = target - current;

          if (Math.abs(distance) <= 0.35) {
            this.slider.scrollLeft = target;
            this.wheelAnimationFrame = null;
            this.tabWheelLastFrameTime = 0;
            return;
          }

          // Time-based exponential easing stays consistent on 60/90/120/144 Hz
          // displays. A ~55 ms time constant feels responsive while smoothing the
          // large, discrete deltas produced by a conventional mouse wheel.
          const elapsed = Math.min(50, Math.max(1, currentTime - this.tabWheelLastFrameTime));
          this.tabWheelLastFrameTime = currentTime;
          const easing = 1 - Math.exp(-elapsed / 55);

          this.slider.scrollLeft = current + (distance * easing);
          this.wheelAnimationFrame = window.requestAnimationFrame(animateTabWheel);
        };

        this.wheelAnimationFrame = window.requestAnimationFrame(animateTabWheel);
      }

      return;
    }

    // Consume the inertial tail of the gesture while the current card is moving.
    if (this.wheelStepLocked) {
      event.preventDefault();
      this.scheduleProductCarouselWheelUnlock();
      return;
    }

    let direction = Math.sign(dominantDelta);

    // Tab collections get a small intentional-gesture threshold. This filters
    // the tiny first deltas from high-resolution trackpads that made a full
    // one-card step feel twitchy. Other existing carousels retain their prior
    // immediate one-step behaviour.
    if (this.isTabCollectionWheelCarousel) {
      const now = performance.now();

      if (
        now - this.wheelLastEventTime > 140 ||
        (this.wheelLastDirection && direction !== this.wheelLastDirection)
      ) {
        this.wheelDeltaAccumulator = 0;
      }

      this.wheelLastEventTime = now;
      this.wheelLastDirection = direction;
      this.wheelDeltaAccumulator += dominantDelta;

      const gestureThreshold = 14;
      if (Math.abs(this.wheelDeltaAccumulator) < gestureThreshold) {
        const possibleTarget = this.getProductCarouselStepTarget(direction, this.slider.scrollLeft);
        if (possibleTarget !== null) event.preventDefault();
        return;
      }

      direction = Math.sign(this.wheelDeltaAccumulator);
    }

    const target = this.getProductCarouselStepTarget(direction, this.slider.scrollLeft);

    // At either end, hand the wheel back to normal vertical page scrolling.
    if (target === null) {
      this.wheelDeltaAccumulator = 0;
      this.wheelLastDirection = 0;
      return;
    }

    event.preventDefault();
    this.disableProductCarouselSnap();

    this.wheelDeltaAccumulator = 0;
    this.wheelStepDirection = direction;
    this.wheelStepLocked = true;
    this.wheelSnapTarget = target;
    this.scheduleProductCarouselWheelUnlock();
    this.animateProductCarouselTo(target);
  }

  animateProductCarouselTo(target) {
    if (this.wheelAnimationFrame) {
      window.cancelAnimationFrame(this.wheelAnimationFrame);
      this.wheelAnimationFrame = null;
    }

    if (this.isTabCollectionWheelCarousel) {
      // Browser-native smooth scrolling is compositor-driven and avoids the
      // small frame-time variations caused by assigning scrollLeft manually.
      this.slider.scrollTo({ left: target, behavior: 'smooth' });

      const startTime = performance.now();
      let stableFrames = 0;
      let previousPosition = this.slider.scrollLeft;

      const watchForSettle = (currentTime) => {
        const currentPosition = this.slider.scrollLeft;
        const targetDistance = Math.abs(target - currentPosition);
        const frameMovement = Math.abs(currentPosition - previousPosition);

        if (targetDistance <= 0.75 && frameMovement <= 0.2) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }

        previousPosition = currentPosition;

        if (stableFrames >= 3 || currentTime - startTime > 700) {
          if (Math.abs(this.slider.scrollLeft - target) > 0.75) {
            this.slider.scrollTo({ left: target, behavior: 'auto' });
          }

          this.wheelAnimationFrame = null;
          this.wheelSnapTarget = target;

          if (!this.wheelGestureReleaseTimer) {
            this.unlockProductCarouselWheel();
          }
          return;
        }

        this.wheelAnimationFrame = window.requestAnimationFrame(watchForSettle);
      };

      this.wheelAnimationFrame = window.requestAnimationFrame(watchForSettle);
      return;
    }

    // Preserve the existing exact-step animation outside the homepage tab collections.
    const start = this.slider.scrollLeft;
    const distance = target - start;
    const duration = 340;
    const startTime = performance.now();
    const easeInOut = (progress) => progress * progress * (3 - 2 * progress);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);
      const easedProgress = easeInOut(progress);

      this.slider.scrollLeft = start + distance * easedProgress;

      if (progress < 1) {
        this.wheelAnimationFrame = window.requestAnimationFrame(animate);
        return;
      }

      this.slider.scrollLeft = target;
      this.wheelAnimationFrame = null;
      this.wheelSnapTarget = target;

      if (!this.wheelGestureReleaseTimer) {
        this.unlockProductCarouselWheel();
      }
    };

    this.wheelAnimationFrame = window.requestAnimationFrame(animate);
  }

  scheduleProductCarouselWheelUnlock() {
    window.clearTimeout(this.wheelGestureReleaseTimer);
    this.wheelGestureReleaseTimer = window.setTimeout(() => {
      this.wheelGestureReleaseTimer = null;

      if (!this.wheelAnimationFrame) {
        this.unlockProductCarouselWheel();
      }
    }, 110);
  }

  unlockProductCarouselWheel() {
    this.wheelStepLocked = false;
    this.wheelStepDirection = 0;
    this.wheelDeltaAccumulator = 0;
    this.wheelLastDirection = 0;

    if (this.hasProductCarouselTouchInput()) {
      this.enableProductCarouselTouchSwipe();
    }
  }

  hasProductCarouselTouchInput() {
    const mediaQueryMatches = Boolean(
      this.productCarouselTouchQuery && this.productCarouselTouchQuery.matches
    );
    const hasTouchPoints = Number(navigator.maxTouchPoints || 0) > 0;

    return mediaQueryMatches || hasTouchPoints;
  }

  updateProductCarouselInteractionMode() {
    const touchMode = this.hasProductCarouselTouchInput();

    this.classList.toggle('product-carousel--touch-swipe', touchMode);
    this.cancelProductCarouselWheel();

    // The homepage tab collection is a true free-scrolling strip on every
    // input type. Do not align it to product boundaries on resize or mode changes.
    if (this.isTabCollectionWheelCarousel) {
      this.slider.style.setProperty('scroll-behavior', 'auto', 'important');
      this.slider.style.setProperty('scroll-snap-type', 'none', 'important');
      this.slider.style.setProperty('touch-action', touchMode ? 'pan-x pan-y' : 'pan-y', 'important');

      if (touchMode) {
        this.slider.style.setProperty('-webkit-overflow-scrolling', 'touch');
      } else {
        this.slider.style.removeProperty('-webkit-overflow-scrolling');
      }
      return;
    }

    this.alignProductCarouselToNearestSnapPosition();

    if (touchMode) {
      this.enableProductCarouselTouchSwipe();
      return;
    }

    // On pointer-only devices, JavaScript supplies the exact card stops and
    // native snap stays off to prevent the browser from competing with the
    // wheel animation.
    this.disableProductCarouselSnap();
  }

  alignProductCarouselToNearestSnapPosition() {
    const positions = this.getProductCarouselSnapPositions();
    if (positions.length === 0) return;

    const currentPosition = this.slider.scrollLeft;
    const nearestPosition = positions.reduce((nearest, position) => {
      return Math.abs(position - currentPosition) < Math.abs(nearest - currentPosition)
        ? position
        : nearest;
    }, positions[0]);

    this.slider.scrollLeft = nearestPosition;
  }

  enableProductCarouselTouchSwipe() {
    this.slider.style.setProperty('scroll-behavior', 'smooth', 'important');
    this.slider.style.setProperty('scroll-snap-type', 'x mandatory', 'important');
    this.slider.style.setProperty('touch-action', 'pan-x pan-y', 'important');
    this.slider.style.setProperty('-webkit-overflow-scrolling', 'touch');
  }

  disableProductCarouselSnap() {
    this.slider.style.setProperty('scroll-behavior', 'auto', 'important');
    this.slider.style.setProperty('scroll-snap-type', 'none', 'important');
    this.slider.style.setProperty('touch-action', 'pan-y', 'important');
    this.slider.style.removeProperty('-webkit-overflow-scrolling');
  }

  cancelProductCarouselWheel() {
    window.clearTimeout(this.wheelGestureReleaseTimer);
    this.wheelGestureReleaseTimer = null;
    this.wheelStepLocked = false;
    this.wheelStepDirection = 0;
    this.wheelSnapTarget = null;
    this.wheelDeltaAccumulator = 0;
    this.wheelLastDirection = 0;
    this.tabWheelTarget = null;
    this.tabWheelLastFrameTime = 0;

    if (this.wheelAnimationFrame) {
      window.cancelAnimationFrame(this.wheelAnimationFrame);
      this.wheelAnimationFrame = null;
    }
  }

  enableSwipeDrag() {
    this.isDown = false;
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;

    this.slider.addEventListener('mousedown', this.onDragStart.bind(this));
    this.slider.addEventListener('mousemove', this.onDragMove.bind(this));
    this.slider.addEventListener('mouseup', this.onDragEnd.bind(this));
    this.slider.addEventListener('mouseleave', this.onDragEnd.bind(this));
    this.slider.addEventListener('click', this.preventClickAfterDrag.bind(this), true);
  }

  shouldDisableDesktopDrag() {
    return window.matchMedia && window.matchMedia('(min-width: 990px)').matches && this.matches(this.desktopDragDisabledSelector);
  }

  onDragStart(event) {
    if (event.button !== 0) return;
    if (this.shouldDisableDesktopDrag()) return;

    this.isDown = true;
    this.isDragging = false;
    this.startX = event.pageX - this.slider.offsetLeft;
    this.scrollLeft = this.slider.scrollLeft;
  }

  onDragMove(event) {
    if (!this.isDown) return;

    const x = event.pageX - this.slider.offsetLeft;
    const walk = x - this.startX;

    if (Math.abs(walk) > 5) {
      this.isDragging = true;
      this.slider.classList.add('is-dragging');
      event.preventDefault();
      this.slider.scrollLeft = this.scrollLeft - walk;
    }
  }

  onDragEnd() {
    if (!this.isDown) return;

    this.isDown = false;
    window.setTimeout(() => {
      this.slider.classList.remove('is-dragging');
      this.isDragging = false;
    }, 0);
  }

  preventClickAfterDrag(event) {
    if (!this.isDragging) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

customElements.define('slider-component', SliderComponent);
